// Обновление приложения (barter-025, по образцу Айлы lib/update.js).
// vite-plugin-pwa (autoUpdate) ставит новый SW в фоне: браузер сам проверяет
// sw.js при навигации и раз в сутки — мы форсируем проверку чаще (интервал
// + возврат видимости), а вместо мгновенного reload по controllerchange
// показываем тост «Доступно обновление»: страница продолжает работать старым
// кодом, пока пользователь сам не нажмёт «Обновить» (данные в Dexie/localStorage
// и текущий URL переживают перезагрузку — состояние не теряется).
//
// Манифест «Центра обновлений Aila» здесь не используется: эндпоинт Айлы не
// отдаёт CORS-заголовки для чужого Origin, fetch из браузера Бартера не пройдёт;
// факта нового SW (см. main.tsx → UPDATE_READY_EVENT) достаточно.

export const UPDATE_READY_EVENT = 'barter:update-ready';

export const UPDATE_CHECK_INTERVAL_MS = 10 * 60 * 1000;
const THROTTLE_MS = 45_000;

let lastCheck = 0;
let inited = false;

/** только для юнит-тестов: сбросить троттл/флаг между сценариями */
export function _resetForTests(): void {
  lastCheck = 0;
  inited = false;
}

/**
 * semver-подобное сравнение (без префиксов/суффиксов): true — remote строго
 * больше own (0.9.1 < 0.9.2 < 0.10.0 < 1.0.0). Числа сравниваем как числа,
 * разную длину добиваем нулями.
 */
export function isNewerVersion(remote: string, own: string): boolean {
  const parse = (v: string): number[] =>
    String(v)
      .trim()
      .replace(/^v/i, '')
      .split('.')
      .map((p) => parseInt(p, 10) || 0);
  const a = parse(remote);
  const b = parse(own);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const d = (a[i] || 0) - (b[i] || 0);
    if (d !== 0) return d > 0;
  }
  return false;
}

/**
 * Показывать ли тост «Доступно обновление» по смене контроллера SW.
 * Первый SW (страница загрузилась без контроллера, hadController=false) просто
 * берёт управление — это установка, а не обновление, тост не нужен. Смена
 * контроллера у уже контролируемой страницы — новая сборка с сервера.
 */
export function shouldShowUpdateToast(hadController: boolean): boolean {
  return hadController;
}

/** форсировать браузерную проверку sw.js (троттл 45 c). Новый SW поставится
 *  в фоне (autoUpdate + skipWaiting) → controllerchange → тост. */
export async function checkForSwUpdate(
  win: Window | null = typeof window !== 'undefined' ? window : null,
): Promise<void> {
  const now = Date.now();
  if (now - lastCheck < THROTTLE_MS) return;
  lastCheck = now;
  try {
    const reg = await win?.navigator?.serviceWorker?.getRegistration?.();
    await reg?.update?.();
  } catch {
    /* офлайн / нет SW — тихо, проверим в следующий раз */
  }
}

/**
 * Слушать смену контроллера и проверять обновления по расписанию.
 * Вызывать один раз после монтирования приложения (App). Повторный вызов — no-op.
 */
export function initUpdateWatch(win: Window = typeof window !== 'undefined' ? window : {} as Window): void {
  if (inited || !win?.addEventListener) return;
  inited = true;
  const sw = win.navigator?.serviceWorker;
  if (!sw) return;

  // Страница, загруженная до первого SW, получит controllerchange при его
  // установке — это не обновление (см. shouldShowUpdateToast).
  const hadController = !!sw.controller;

  sw.addEventListener('controllerchange', () => {
    if (!shouldShowUpdateToast(hadController)) return;
    try {
      win.dispatchEvent(new Event(UPDATE_READY_EVENT));
    } catch {
      /* очень старый движок — обновимся при следующем открытии */
    }
  });

  // Браузер проверяет sw.js сам при навигации и раз в сутки; форсируем чаще:
  // каждые 10 минут и при каждом возврате видимости (троттл внутри).
  win.setInterval(() => void checkForSwUpdate(win), UPDATE_CHECK_INTERVAL_MS);
  win.addEventListener('visibilitychange', () => {
    if (win.document?.visibilityState === 'visible') void checkForSwUpdate(win);
  });
}

/** тап по кнопке тоста: перезагрузка на уже активированный новый SW */
export function applyUpdate(win: Window = typeof window !== 'undefined' ? window : {} as Window): void {
  try {
    win.location?.reload?.();
  } catch {
    /* перезагрузка недоступна — обновимся при следующем открытии */
  }
}
